/**
 * The account statement's period picker, reduced to arithmetic.
 *
 * Two things live here and nothing else: turning a named period into the
 * `{ from, to }` pair `GET /api/portal/statement` takes, and turning the
 * `?period=` value in the address bar back into a name. Both are React-free so
 * they can be exercised without a renderer, and `now` is an argument rather than
 * a call to `Date.now()` so a test is not a race against the calendar.
 *
 * ── EVERY BOUNDARY IS UTC ──────────────────────────────────────────────────
 *
 * The API anchors `from` and `to` at UTC midnight and closes the window at
 * 23:59:59.999 UTC (see `apps/api/src/modules/portal/lib/statement.ts`).
 * Deriving the same boundaries from the browser's local calendar would move them
 * by up to a day for a reader in another zone: "this year" would start on 31
 * December for half of them, and the opening balance would then carry one
 * invoice that the reader can see listed below it. Same clock on both sides, no
 * such class of bug.
 */

/** The periods the statement offers, in picker order. */
export const STATEMENT_PERIODS = ["this-year", "last-year", "last-12-months", "all"] as const;

export type StatementPeriod = (typeof STATEMENT_PERIODS)[number];

/**
 * What an unnamed period means.
 *
 * The current year, which is also what the API defaults a missing `from` to. The
 * two agreeing matters: a first visit with no `?period=` must render the same
 * window whether the browser sends the dates or lets the server pick them.
 */
export const DEFAULT_STATEMENT_PERIOD: StatementPeriod = "this-year";

/**
 * How far back the API will answer, in years.
 *
 * Mirrors `MAX_YEARS` in `apps/api/src/modules/portal/statement.routes.ts`,
 * where it is a sanity bound on a query that walks every invoice and payment of
 * one company. It is duplicated here rather than fetched because the "all"
 * period below has to ASK for a window the server will grant unchanged.
 */
export const STATEMENT_MAX_YEARS = 20;

/** An inclusive window, both ends `YYYY-MM-DD`, as the API takes them. */
export interface StatementWindow {
  from: string;
  to: string;
}

/**
 * A UTC calendar day as `YYYY-MM-DD`.
 *
 * `Date.UTC` normalises overflow, so 29 February minus one year lands on 1 March
 * rather than throwing or silently producing an invalid string. That costs the
 * window a single day once every four years at one edge, which is the cheapest
 * of the available wrong answers.
 */
function isoDay(year: number, monthIndex: number, day: number): string {
  return new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10);
}

/**
 * The window a named period asks for, relative to `now`.
 *
 * Both ends are always sent. Leaving one off and letting the server decide would
 * mean the page prints a period it did not choose, and for "all" it would mean
 * the exact opposite of what the reader clicked - see the case below.
 */
export function periodWindow(period: StatementPeriod, now: Date): StatementWindow {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();
  const today = isoDay(year, month, day);

  switch (period) {
    case "last-year":
      // A closed year, not "up to today last year". A bookkeeper reconciling
      // 2025 wants all of 2025 and nothing of 2026.
      return { from: isoDay(year - 1, 0, 1), to: isoDay(year - 1, 11, 31) };

    case "last-12-months":
      // The same date one year back. Because the API's window is inclusive at
      // both ends this is one day over twelve months; including the anniversary
      // day is the friendlier end to err on, since a document issued exactly a
      // year ago is the one a reader would notice missing.
      return { from: isoDay(year - 1, month, day), to: today };

    case "all":
      // "Everything" sends a REAL date, and the date is deliberately the oldest
      // one the API will accept rather than something safely ancient.
      //
      // Two wrong versions of this were available. Sending no `from` at all is
      // the worst: the route defaults a missing `from` to 1 January of the
      // current year, so "everything" would quietly render "this year" - the
      // opposite of what was clicked, with nothing on screen to reveal it.
      // Sending an obviously old date (1970, say) is better but still asks for
      // something the server will silently rewrite, because it clamps any start
      // earlier than `to` minus `STATEMENT_MAX_YEARS` years.
      //
      // So this asks for exactly the clamp: the request the browser sends and
      // the window the server answers with are the same window, and the period
      // caption under the totals can be read as a fact rather than as a hope.
      // The honest name for this period is "as far back as the ledger is kept",
      // and that is what the label says.
      return { from: isoDay(year - STATEMENT_MAX_YEARS, month, day), to: today };

    case "this-year":
      return { from: isoDay(year, 0, 1), to: today };
  }
}

/**
 * The period named in the URL, or the default.
 *
 * The raw value is untrusted in the ordinary way - `?period=` is something
 * anybody can type, and a stale bookmark can carry a name this file no longer
 * offers. Anything unrecognised falls back rather than throwing, because a
 * mistyped parameter should cost the reader a different window, not the page.
 */
export function readPeriod(raw: string | null | undefined): StatementPeriod {
  const value = (raw ?? "").trim();
  return (STATEMENT_PERIODS as readonly string[]).includes(value)
    ? (value as StatementPeriod)
    : DEFAULT_STATEMENT_PERIOD;
}
